import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect, useMemo } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Box, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchHolidays } from "../../../redux/userDataSlice";
import { convertHolidaysToEvents, convertToEvents, formatCalendarTitle, isPastMonth } from "./utils";
import { DEFAULT_TIME_FILTERS } from "./constants";
import CalendarHeader from "./components/CalendarHeader";
import Sidebar from "./components/Sidebar";
import EventContent from "./components/EventContent";
import EventDrawer from "./components/EventDrawer";
import DailyEventsModal from "./components/DailyEventsDrawer"; // ✅ Using it as Modal now
import "./advancecalender.css";
import CampaignPreviewDrawer from "../../../pages/EmailMarketingReport/CampaignPreviewDrawer";
import { refreshMarketingCalendar, syncMondayTask } from "../../../redux/marketingCalendarSlice";
import { toast } from "../../../common_components/Toast/ToastUtils";
import ConfirmActionModal from "../../../common_components/ConfirmActionModal";
import { Dropdown } from "../../../common_components/FormInputs";
import { selectEntityType, selectUser, selectCanManageProposals, selectCanDraftEmailApproval, selectCanCalendarSync, selectSendForApproval } from "../../../redux/authSlice";
export default function AdvancedCalendar({ calendarData, calendarMomData, calendarYoyData, tabs, selectedMonth, clientId, connectionId, onMonthChange, onSendForApproval, onApproveAll, isApprovalEnabled = true, isMondayEnabled = true, }) {
    const dispatch = useDispatch();
    const holidays = useSelector((state) => state.userData.holidays);
    const calendarRef = useRef(null);
    const user = useSelector(selectUser);
    const canManageProposals = useSelector(selectCanManageProposals);
    const canDraftEmailApproval = useSelector(selectCanDraftEmailApproval);
    const canSendForApproval = useSelector(selectSendForApproval);
    const canCalendarSync = useSelector(selectCanCalendarSync);
    const entityType = useSelector(selectEntityType);
    const [selectedTab, setSelectedTab] = useState('all');
    const [view, setView] = useState("dayGridMonth");
    const [calendarTitle, setCalendarTitle] = useState("");
    // ✅ Event Drawer State (for cell click)
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [title, setTitle] = useState("");
    const [bulkSyncLoading, setBulkSyncLoading] = useState(false);
    const [selectedPendingStatus, setSelectedPendingStatus] = useState("Pending Design");
    const [timeFilters, setTimeFilters] = useState(() => {
        const saved = localStorage.getItem('mc_timeFilters');
        if (saved) {
            try {
                return JSON.parse(saved);
            }
            catch (e) { }
        }
        return DEFAULT_TIME_FILTERS;
    });
    const [otherFilters, setOtherFilters] = useState(() => {
        const saved = localStorage.getItem('mc_otherFilters');
        if (saved) {
            try {
                return JSON.parse(saved);
            }
            catch (e) { }
        }
        return { hideApproved: false, hideHoliday: false };
    });
    const [openSections, setOpenSections] = useState({ timePeriod: true, other: true });
    useEffect(() => {
        localStorage.setItem('mc_timeFilters', JSON.stringify(timeFilters));
    }, [timeFilters]);
    useEffect(() => {
        localStorage.setItem('mc_otherFilters', JSON.stringify(otherFilters));
    }, [otherFilters]);
    // ✅ Preview Drawer State (for eye icon click)
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewRowData, setPreviewRowData] = useState(null);
    const [previewChannelKey, setPreviewChannelKey] = useState("campaigns");
    // ✅ Daily Events Modal State
    const [dailyEventsOpen, setDailyEventsOpen] = useState(false);
    const [dailyEventsDate, setDailyEventsDate] = useState(null);
    const [dailyEvents, setDailyEvents] = useState([]);
    // ✅ Sync Modal State
    const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
    const [proposalsToSync, setProposalsToSync] = useState([]);
    const getApi = () => calendarRef.current?.getApi();
    const updateTitle = () => {
        const api = getApi();
        if (!api)
            return;
        setCalendarTitle(formatCalendarTitle(api));
        if (api.view.type === "dayGridMonth" && onMonthChange) {
            const d = api.getDate();
            onMonthChange(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
        }
    };
    const isAllApproved = useMemo(() => {
        const data = calendarData;
        if (!data || typeof data !== "object")
            return true;
        let campaigns = Object.values(data)
            .flatMap((day) => day?.campaignData || [])
            .filter((item) => ["draft", "proposal"].includes(item?.type?.toLowerCase()));
        if (entityType === "client") {
            campaigns = campaigns.filter((item) => item?.visible_to_client === true);
        }
        if (campaigns.length === 0)
            return true;
        return campaigns.every((item) => item?.status?.toLowerCase() === "approved");
    }, [calendarData, entityType]);
    const isAllVisibleToClient = useMemo(() => {
        const data = calendarData;
        if (!data || typeof data !== "object")
            return false;
        const campaigns = Object.values(data)
            .flatMap((day) => day?.campaignData || [])
            .filter((item) => ["draft", "proposal"].includes(item?.type?.toLowerCase()));
        if (campaigns.length === 0)
            return false;
        return campaigns.every((item) => item?.visible_to_client === true);
    }, [calendarData]);
    // Events
    const getAllEvents = () => {
        let events = [];
        if (!otherFilters.hideHoliday)
            events.push(...convertHolidaysToEvents((holidays || []), clientId));
        // Always push current campaigns, toggle performance badges based on checkbox
        events.push(...convertToEvents(calendarData, 'current', selectedTab, selectedMonth, timeFilters.current.checked));
        if (timeFilters.mom.checked)
            events.push(...convertToEvents(calendarMomData, 'mom', selectedTab, selectedMonth, true));
        if (timeFilters.yoy.checked)
            events.push(...convertToEvents(calendarYoyData, 'yoy', selectedTab, selectedMonth, true));
        return events;
    };
    const getAllProposalEvents = () => {
        return filteredEvents.filter(event => {
            const type = String(event.extendedProps?.type || '').toLowerCase();
            return type === 'proposal';
        });
    };
    const filteredEvents = getAllEvents().filter(e => {
        const status = e.extendedProps?.status?.toLowerCase();
        const type = String(e.extendedProps?.type || '').toLowerCase();
        const visible = e.extendedProps?.visible_to_client;
        // ❌ Hide approved if toggle ON
        if (otherFilters.hideApproved && status === 'approved')
            return false;
        // ✅ Client condition
        if (entityType === "client") {
            if ((type === "draft" || type === "proposal") && !visible) {
                return false;
            }
        }
        return true;
    });
    // Effects
    useEffect(() => { dispatch(fetchHolidays({ month: selectedMonth })); }, [dispatch, selectedMonth]);
    useEffect(() => {
        if (calendarRef.current) {
            const api = getApi();
            setCalendarTitle(api.view.title);
            setTimeout(() => api.updateSize(), 150);
        }
    }, []);
    useEffect(() => {
        if (selectedMonth && calendarRef.current) {
            getApi().gotoDate(`${selectedMonth}-01`);
            updateTitle();
        }
    }, [selectedMonth]);
    // Handlers
    // FullCalendar hands back local-time Date objects for day cells; converting
    // via toISOString() shifts the date across UTC offsets ahead of UTC, so we
    // format using local getters instead of round-tripping through UTC.
    const toLocalYMD = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    const isDateInViewedMonth = (dateStr) => {
        if (!selectedMonth)
            return true;
        return dateStr.startsWith(selectedMonth);
    };
    const isCurrentMonth = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const currentMonthStr = `${year}-${month}`;
        return selectedMonth === currentMonthStr;
    };
    const handleViewChange = (val) => {
        setView(val);
        const api = getApi();
        if (!api)
            return;
        api.changeView(val);
        // ✅ If Day/Week selected and it's the current month, go to today
        if ((val === "dayGridWeek" || val === "dayGridDay") && isCurrentMonth()) {
            api.today();
        }
        updateTitle();
    };
    // ✅ Cell click - Current/Future month → Open EventDrawer
    const handleDateSelect = (info) => {
        // Block if not in viewed month
        if (!isDateInViewedMonth(info.startStr))
            return;
        // Past month block
        if (isPastMonth(info.startStr)) {
            return;
        }
        if (!canManageProposals) {
            return;
        }
        setSelectedEvent({
            id: Date.now().toString(),
            title: "New Event",
            start: info.startStr,
            end: info.endStr || info.startStr,
            extendedProps: {
                isNewEvent: true, // ✅ Flag for new event
                selectedDate: info.startStr
            }
        });
        setTitle("New Event");
        setDrawerOpen(true); // ✅ Open EventDrawer
    };
    // ✅ Event click - Past month block
    const handleEventClick = (clickInfo) => {
        const props = clickInfo.event.extendedProps || {};
        if (props.type === 'holiday' || props.isHoliday)
            return;
        if (props.type === 'performance')
            return;
        // Block if not in viewed month
        if (!isDateInViewedMonth(clickInfo.event.startStr))
            return;
        if (isPastMonth(clickInfo.event.startStr))
            return;
        setSelectedEvent({
            id: clickInfo.event.id,
            title: clickInfo.event.title,
            start: clickInfo.event.startStr,
            end: clickInfo.event.endStr,
            backgroundColor: clickInfo.event.backgroundColor,
            extendedProps: props
        });
        setTitle(clickInfo.event.title);
        setDrawerOpen(true);
        setDailyEventsOpen(false); // ✅ Close modal when opening event drawer
    };
    // ✅ Eye icon click - Open CampaignPreviewDrawer
    const handleViewClick = (e, campaignData, channelKey) => {
        setDailyEventsOpen(false); // ✅ Close modal when viewing
        const type = String(campaignData?.type || '').toLowerCase();
        // ✅ If proposal -> open EventDrawer with prefilled data
        if (type === 'proposal') {
            e.stopPropagation();
            setSelectedEvent({
                id: campaignData.id,
                title: campaignData.name,
                start: campaignData.startDate,
                end: campaignData.endDate,
                extendedProps: {
                    ...campaignData,
                    type: "proposal"
                }
            });
            setTitle(campaignData.name || "Edit Proposal");
            setDrawerOpen(true);
            return;
        }
        e.stopPropagation();
        setPreviewRowData(campaignData);
        setPreviewChannelKey(channelKey);
        setPreviewOpen(true);
    };
    // ✅ Close EventDrawer
    const handleDrawerClose = () => {
        setDrawerOpen(false);
        setSelectedEvent(null);
        setTitle("");
    };
    // ✅ Close preview drawer
    const handlePreviewClose = () => {
        setPreviewOpen(false);
        setPreviewRowData(null);
    };
    const handleBulkMondaySync = () => {
        const proposals = getAllProposalEvents();
        if (proposals.length === 0) {
            toast.error("No proposals found to sync!");
            return;
        }
        setProposalsToSync(proposals);
        setSelectedPendingStatus("Pending Design");
        setIsSyncModalOpen(true);
    };
    const executeBulkMondaySync = async () => {
        setIsSyncModalOpen(false);
        setBulkSyncLoading(true);
        let succeeded = 0;
        const failed = [];
        try {
            // ✅ Loop through all proposals and sync each one. Each proposal is synced
            // independently — one failing (e.g. a Monday permission error, or a stale
            // item id) must not stop the rest of the batch from being attempted.
            for (const proposal of proposalsToSync) {
                const p = proposal.extendedProps || {};
                const taskName = p.name || proposal.title;
                const groupName = p.startDate
                    ? new Date(p.startDate).toLocaleString("en-US", { month: "long", year: "numeric" })
                    : new Date().toLocaleString("en-US", { month: "long", year: "numeric" });
                const getValidMondayStatus = (status) => {
                    const statusLower = status.toLowerCase();
                    if (statusLower === "pending" || statusLower === "pending_dev") {
                        return selectedPendingStatus;
                    }
                    const statusMap = {
                        "completed": "Completed",
                        "Completed": "Completed",
                        "done": "Completed",
                        "Done": "Completed",
                        "sent_for_approval": "Sent for Approval",
                        "Sent for Approval": "Sent for Approval",
                        "scheduled": "Scheduled",
                        "Scheduled": "Scheduled",
                        "pending_copy": "Pending Copy",
                        "Pending Copy": "Pending Copy",
                        "approved": "Approved",
                        "Approved": "Approved",
                        "creative_complete": "Creative Complete",
                        "Creative Complete": "Creative Complete",
                        "cancelled": "Cancelled",
                        "Cancelled": "Cancelled",
                        "pending_design": "Pending Design",
                        "Pending Design": "Pending Design",
                        "in_progress": "In Progress",
                        "In Progress": "In Progress",
                        "not_approved": "Not Approved",
                        "Not Approved": "Not Approved",
                        "rejected": "Not Approved",
                        "Rejected": "Not Approved",
                        "pending_client": "Pending Client",
                        "Pending Client": "Pending Client",
                        "on_hold": "On Hold",
                        "On Hold": "On Hold",
                    };
                    return statusMap[status] || "Pending Dev";
                };
                try {
                    await dispatch(syncMondayTask({
                        task: taskName,
                        sendDate: p.startDate,
                        creativeDueDate: p.endDate,
                        status: getValidMondayStatus(p.status || "pending"),
                        code: p.discountCode,
                        groupName: groupName,
                        discountCode: p.discountCode,
                        whoIsDesigning: p.whoIsDesigning,
                        whoIsSending: p.whoIsSending,
                        clientProvidingAssets: p.clientProvidingAssets ? "Yes" : "No",
                        dropboxUrl: p.assetUrl,
                        promotion: p.content?.promotion,
                        themeSummary: p.content?.themeSummary,
                        productFeatures: p.content?.productFeatures,
                        proposalId: p.id || p._id,
                        mondayTaskId: p.mondayTaskId, // ✅ If already synced, update
                        hideToast: true // ✅ Suppress slice toast
                    })).unwrap();
                    succeeded++;
                }
                catch (error) {
                    console.error(`Monday sync failed for proposal "${taskName}"`, error);
                    failed.push(taskName);
                }
            }
            if (failed.length === 0) {
                toast.success(`${succeeded} proposal(s) synced to Monday.com successfully!`);
            }
            else if (succeeded === 0) {
                toast.error(`Failed to sync ${failed.length} proposal(s). Please try again or contact support.`);
            }
            else {
                toast.error(`Synced ${succeeded} of ${proposalsToSync.length} proposal(s). Failed: ${failed.join(", ")}`);
            }
            await dispatch(refreshMarketingCalendar());
        }
        catch (error) {
            console.error("Bulk Monday sync failed", error);
            toast.error("Some proposals failed to sync. Please try again or contact support.");
        }
        finally {
            setBulkSyncLoading(false);
        }
    };
    const [sidebarHeight, setSidebarHeight] = useState('auto');
    const calendarContainerRef = useRef(null);
    // ✅ Sync Sidebar height with Calendar height and handle DOM updates for width issues
    useEffect(() => {
        const updateLayout = () => {
            if (calendarRef.current) {
                const api = getApi();
                if (api)
                    api.updateSize();
            }
            if (calendarContainerRef.current) {
                const height = calendarContainerRef.current.offsetHeight;
                // Enforce a minimum height so the sidebar isn't cut off when the calendar is small (e.g., in Day view)
                if (height)
                    setSidebarHeight(Math.max(height, 600));
            }
        };
        const timer1 = setTimeout(updateLayout, 150);
        const timer2 = setTimeout(updateLayout, 500); // extra delay for safe measurement
        window.addEventListener('resize', updateLayout);
        // ✅ Add ResizeObserver to watch container width changes (e.g., when global sidebar opens/closes)
        let observer = null;
        if (calendarContainerRef.current) {
            observer = new ResizeObserver(() => {
                window.requestAnimationFrame(() => {
                    updateLayout();
                });
            });
            observer.observe(calendarContainerRef.current);
        }
        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            window.removeEventListener('resize', updateLayout);
            if (observer) {
                observer.disconnect();
            }
        };
    }, [filteredEvents, view, selectedMonth, calendarData, openSections]);
    return (_jsxs(Box, { children: [_jsx(Box, { sx: { position: 'sticky', top: 0, zIndex: 100, bgcolor: '#F9FAFB', py: 1, mt: -1 }, children: _jsx(CalendarHeader, { title: calendarTitle, view: view, tabs: tabs, selectedTab: selectedTab, isApprovalEnabled: canDraftEmailApproval, isSendForApprovalEnabled: canSendForApproval, isMondayEnabled: canCalendarSync && getAllProposalEvents().length > 0, bulkSyncLoading: bulkSyncLoading, entityType: entityType, onPrev: () => { getApi()?.prev(); updateTitle(); }, onNext: () => { getApi()?.next(); updateTitle(); }, onViewChange: handleViewChange, onTabChange: setSelectedTab, onSendForApproval: onSendForApproval, onApproveAll: onApproveAll, onBulkMondaySync: handleBulkMondaySync, isAllApproved: isAllApproved, isAllVisibleToClient: isAllVisibleToClient }) }), _jsxs(Box, { sx: { display: 'flex', gap: 1, alignItems: 'flex-start', width: '100%' }, children: [_jsx(Sidebar, { timeFilters: timeFilters, otherFilters: otherFilters, openSections: openSections, onTimeFilterChange: setTimeFilters, onOtherFilterChange: setOtherFilters, onSectionToggle: (s) => setOpenSections({ ...openSections, [s]: !openSections[s] }), maxHeight: sidebarHeight }), _jsx(Box, { ref: calendarContainerRef, sx: {
                            flex: 1,
                            minWidth: 0,
                            '& .fc': {
                                width: '100%',
                                minHeight: (view === "dayGridWeek" || view === "dayGridDay") ? "54vh" : "auto",
                                bgcolor: '#fff',
                                borderRadius: '8px',
                                border: '1px solid #EAECF0',
                                overflow: 'hidden'
                            },
                            '& .fc-daygrid-day-frame': {
                                minHeight: (view === "dayGridWeek" || view === "dayGridDay") ? "54vh" : "auto",
                            }
                        }, children: _jsx(FullCalendar, { ref: calendarRef, plugins: [dayGridPlugin, interactionPlugin], initialView: "dayGridMonth", initialDate: selectedMonth ? `${selectedMonth}-01` : undefined, headerToolbar: false, displayEventTime: false, dayHeaderFormat: { weekday: "short" }, dayPopoverFormat: { weekday: 'short', day: 'numeric' }, selectable: true, editable: false, events: filteredEvents, dayMaxEvents: 6, moreLinkClick: (arg) => {
                                if (!isDateInViewedMonth(toLocalYMD(arg.date)))
                                    return 'none';
                                setDailyEventsDate(arg.date);
                                const nonHolidayEvents = (arg.allSegs || [])
                                    .map((seg) => seg.event)
                                    .filter((e) => {
                                    const props = e.extendedProps || {};
                                    return !props.isHoliday && props.type !== 'holiday';
                                });
                                setDailyEvents(nonHolidayEvents);
                                setDailyEventsOpen(true);
                                return "none"; // ✅ Disable default popover
                            }, moreLinkContent: (arg) => {
                                const nonHolidayHiddenCount = (arg.hiddenSegs || []).filter((seg) => {
                                    const props = seg.event?.extendedProps || {};
                                    return !props.isHoliday && props.type !== 'holiday';
                                }).length;
                                if (nonHolidayHiddenCount <= 0)
                                    return null;
                                return (_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '8px' }, children: [_jsx("span", { children: "View more" }), _jsx("span", { className: "more-count-pill", children: nonHolidayHiddenCount })] }));
                            }, eventContent: (info) => (_jsx(EventContent, { eventInfo: info, onViewClick: handleViewClick, onViewAll: () => {
                                    const date = info.event.start;
                                    if (!date)
                                        return;
                                    const dateStr = toLocalYMD(date);
                                    const eventsForDay = getApi()?.getEvents().filter((e) => e.start && toLocalYMD(e.start) === dateStr) || [];
                                    const nonHolidayEvents = eventsForDay.filter((e) => {
                                        const props = e.extendedProps || {};
                                        return !props.isHoliday && props.type !== 'holiday';
                                    });
                                    setDailyEventsDate(date);
                                    setDailyEvents(nonHolidayEvents);
                                    setDailyEventsOpen(true);
                                } })), eventClick: (e) => handleEventClick(e), select: handleDateSelect, fixedWeekCount: false, eventOrder: "order,id", height: "auto", expandRows: true, contentHeight: undefined, dayCellContent: (arg) => _jsx("div", { className: "fc-daygrid-day-number", children: arg.date.getDate() }) }) })] }), _jsx(EventDrawer, { open: drawerOpen, event: selectedEvent, title: title, clientId: clientId, onClose: handleDrawerClose, selectedMonth: selectedMonth, connectionId: connectionId, user: user, entityType: entityType }), _jsx(CampaignPreviewDrawer, { open: previewOpen, onClose: handlePreviewClose, rowData: previewRowData, channelKey: previewChannelKey, clientId: clientId, showTakeAction: String(previewRowData?.type || '').toLowerCase() === 'draft', connectionId: connectionId, campaignId: previewRowData?._id, currentStatus: previewRowData?.status, entityType: entityType }), _jsx(DailyEventsModal, { open: dailyEventsOpen, onClose: () => setDailyEventsOpen(false), date: dailyEventsDate, events: dailyEvents, onViewClick: handleViewClick, onEventClick: handleEventClick }), _jsx(ConfirmActionModal, { open: isSyncModalOpen, onClose: () => setIsSyncModalOpen(false), onConfirm: executeBulkMondaySync, title: "Sync Proposals", description: `Are you sure you want to sync ${proposalsToSync.length} proposal(s) to Monday.com?`, confirmText: "Sync Now", isDeleteIcon: false, buttonColor: "#12B76A", buttonHoverColor: "#0E9F5D", children: proposalsToSync.some(p => {
                    const st = (p.extendedProps?.status || "pending").toLowerCase();
                    return st === "pending" || st === "pending_dev";
                }) && (_jsxs(Box, { sx: { mt: 2, textAlign: "left" }, children: [_jsx(Typography, { sx: { mb: 1, fontSize: 14, fontWeight: 500, color: "#344054" }, children: "Select Initial Status for Pending Proposals:" }), _jsx(Dropdown, { value: selectedPendingStatus, onChange: (val) => setSelectedPendingStatus(val), options: [
                                { value: "Pending Design", label: "Pending Design" },
                                { value: "Pending Client", label: "Pending Client" }
                            ], usePortal: true })] })) })] }));
}
